import { AccountPlanSection } from './AccountPlanSection';
import { AccountTheme } from './AccountTheme';
import { PageHeading } from '@src/components/PageHeading/PageHeading';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UserAccountInfo } from '@lub0v/santa-utils';
import {
  BoxShell,
  Button,
  KeyIcon,
  MailIcon,
  StarIcon,
  SettingsActionRow,
  SettingsPanel,
  SettingsSection,
  Spinner,
  Stack,
  TrashIcon,
  Typography,
  UserIcon,
} from '@src/components';
import { confirmDeleteAccount } from '@src/pages/account/deleteAccount';
import { EmailField } from '@src/pages/account/EmailField';
import { GuestAccountPage } from '@src/pages/account/GuestAccountPage';
import { NotificationSettings } from '@src/pages/account/NotificationSettings';
import { AccountRecoveryCodes } from './AccountRecoveryCodes';
import { PasswordFields } from '@src/pages/account/PasswordFields';
import { SocialLinks } from '@src/pages/account/SocialLinks';

import { LOGIN_PATH } from '@src/routing/routes';
import { santaClient } from '@src/state/client';
import { useAppSelector } from '@src/state/hooks';

function AccountLoading({ label }: { label: string }) {
  return (
    <BoxShell width="medium" pad="compact" testId="account-loading">
      <Stack gap={4} align="center" wide className="settings">
        <Spinner size="medium" label={label} />
      </Stack>
    </BoxShell>
  );
}

export function AccountPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.session.user);
  const sessionStatus = useAppSelector((state) => state.session.status);
  const [account, setAccount] = useState<UserAccountInfo | null>(null);
  const [challengeToken, setChallengeToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionStatus === 'idle' || sessionStatus === 'loading') {
      return;
    }
    if (!user) {
      navigate(LOGIN_PATH);
    }
  }, [sessionStatus, user, navigate]);

  useEffect(() => {
    if (!user || user.isGuest) {
      return;
    }
    void santaClient.account().then((res) => {
      if (!res.ok || !res.data) {
        setError(t('switcher.actionError'));
        return;
      }
      const otp = (res.data as UserAccountInfo & { otpChallenge?: { challengeToken?: string } })
        .otpChallenge;
      setAccount(res.data);
      setChallengeToken(otp?.challengeToken || '');
    });
  }, [t, user]);

  if (
    sessionStatus === 'idle' ||
    sessionStatus === 'loading' ||
    (!user && sessionStatus !== 'failed')
  ) {
    return <AccountLoading label={t('account.loading')} />;
  }

  if (user?.isGuest) {
    return <GuestAccountPage />;
  }

  if (!account) {
    return (
      <BoxShell width="medium" pad="compact" testId="account-loading">
        <Stack gap={8} wide className="settings">
          <Stack gap={3} as="header" wide className="landscape-sky-content">
            <PageHeading title={<>{t('account.title')}</>} />
            {error ? (
              <Typography as="p" role="meta/error">
                {error}
              </Typography>
            ) : (
              <Spinner size="medium" label={t('account.loading')} />
            )}
          </Stack>
        </Stack>
      </BoxShell>
    );
  }

  return (
    <BoxShell width="medium" pad="compact" testId="account-settings">
      <Stack gap={8} wide className="settings">
        <Stack gap={3} as="header" wide className="landscape-sky-content">
          <PageHeading title={<>{t('account.title')}</>} subtitle={<>{t('account.autosave')}</>} />
          {error ? (
            <Typography as="p" role="meta/error">
              {error}
            </Typography>
          ) : null}
        </Stack>

        <SettingsPanel>
          <AccountPlanSection />
          <SettingsSection
            title={t('account.personal')}
            hint={t('account.personalHint')}
            icon={<UserIcon />}
            iconTone="accent"
            testId="account-personal"
          >
            <EmailField
              account={account}
              challengeToken={challengeToken}
              onAccountChange={(next, token) => {
                setAccount(next);
                setChallengeToken(token);
              }}
            />
          </SettingsSection>

          <SettingsSection
            title={t('account.notifications')}
            hint={t('account.notificationsHint')}
            icon={<MailIcon />}
            iconTone="support"
            testId="account-notifications"
          >
            <NotificationSettings account={account} onAccountChange={setAccount} />
          </SettingsSection>

          <SettingsSection
            title={t('account.social')}
            hint={t('account.socialHint')}
            icon={<StarIcon />}
            iconTone="support"
            testId="account-social"
          >
            <SocialLinks account={account} onAccountChange={setAccount} />
          </SettingsSection>

          <SettingsSection
            title={t('account.password')}
            hint={t('account.passwordHint')}
            icon={<KeyIcon />}
            iconTone="warning"
            testId="account-password"
          >
            <PasswordFields />
          </SettingsSection>

          <SettingsSection
            title={t('account.recoveryTitle')}
            hint={t('account.recoveryHint')}
            icon={<KeyIcon />}
            iconTone="warning"
            testId="account-recovery"
          >
            <AccountRecoveryCodes account={account} />
          </SettingsSection>

          <SettingsSection
            title={t('account.theme')}
            hint={t('account.themeHint')}
            icon={<StarIcon />}
            iconTone="support"
            testId="account-theme"
          >
            <AccountTheme account={account} onChange={setAccount} />
          </SettingsSection>

          <SettingsSection
            title={t('account.delete')}
            hint={t('account.deleteHint')}
            icon={<TrashIcon />}
            iconTone="neutral"
            muted
            testId="account-delete"
          >
            <SettingsActionRow title={t('account.deleteButton')} hint={t('account.deleteRowHint')}>
              <Button
                variant="danger"
                size="small"
                onClick={() => confirmDeleteAccount(t)}
                testId="account-delete-button"
              >
                {t('account.deleteButton')}
              </Button>
            </SettingsActionRow>
          </SettingsSection>
        </SettingsPanel>
      </Stack>
    </BoxShell>
  );
}
